import { memo, useEffect, useRef, SyntheticEvent } from "react";
import Hls from "hls.js";
import { createPlyr, checkIsVideo, checkIsImage } from "helpers";

type PlayerOptions = {
  fullscreen: { enabled: boolean; fallback: boolean; iosNative: boolean };
  vimeo: { fullscreen: boolean };
  controls: string[];
  youtube: {
    noCookie: boolean;
    rel: number;
    showinfo: number;
    iv_load_policy: number;
  };
  quality?: {
    default: number;
    options: number[];
    forced: boolean;
    onChange: (e: any) => void;
  };
  i18n?: { qualityLabel: { [key: number]: string } };
};

const EventCoverPlayer = ({
  videoUrl,
  adaptivePlaylistCover,
}: {
  videoUrl: string | null;
  adaptivePlaylistCover?: boolean;
}) => {
  const playerContainer = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const player = useRef<any>(null);
  const requestPlaybackRef = useRef<(() => void) | null>(null);
  const pendingPlayRef = useRef(false);

  // Determine if the URL is a video or image
  const fileType = videoUrl?.split(".")?.slice(-1)?.[0]?.split("?")?.[0] || "";
  const hasAdaptiveVideoSource = adaptivePlaylistCover === true && !!videoUrl;
  const isVideo = checkIsVideo(fileType) || hasAdaptiveVideoSource;
  const isImage = !hasAdaptiveVideoSource && checkIsImage(fileType);

  const handleInlineOverlayPlay = (event?: SyntheticEvent) => {
    if (event) {
      event.stopPropagation();
    }
    requestPlaybackRef.current?.();
  };

  useEffect(() => {
    // Only initialize player for videos
    const videoElement = playerContainer.current;

    if (!videoUrl || !videoElement || !isVideo) {
      return;
    }

    let isActive = true;

    const enableInlinePlayback = () => {
      if (!isActive) {
        return;
      }
      videoElement.setAttribute("playsinline", "");
      videoElement.setAttribute("webkit-playsinline", "");
      videoElement.playsInline = true;
    };

    const disableNativeControls = () => {
      if (!isInlinePlaybackDevice || !isActive) {
        return;
      }
      videoElement.controls = false;
    };

    const defaultOptions: PlayerOptions = {
      fullscreen: { enabled: true, fallback: true, iosNative: true },
      vimeo: { fullscreen: true },
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "settings",
        "fullscreen",
      ],
      youtube: {
        noCookie: true,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
      },
    };

    let hls: Hls | null = null;
    let removeHlsGestureHandlers: (() => void) | null = null;
    let removeAutoPlayListeners: (() => void) | null = null;
    const isInlinePlaybackDevice =
      /iPad|iPhone|iPod|Instagram|FBAN|FBAV|Telegram/i.test(
        navigator.userAgent
      );

    const setOverlayVisibility = (isVisible: boolean) => {
      if (!overlayRef.current) {
        return;
      }

      overlayRef.current.style.display = isVisible ? "flex" : "none";
    };

    const setupIosStyles = () => {
      if (/iPad|iPhone|iPod/i.test(navigator.userAgent) && isActive) {
        // Set styles early to prevent jumping on first play
        const applyStyles = () => {
          if (isActive) {
            // Set styles that prevent jumping without hiding video
            videoElement.style.objectFit = "contain";
            videoElement.style.width = "100%";
            videoElement.style.height = "auto";
            videoElement.style.margin = "0 auto";
            videoElement.style.display = "block";
          }
        };

        // Apply styles as early as possible - when metadata is loaded
        // This happens before first play, so styles are ready
        if (videoElement.readyState >= 1) {
          // Metadata already loaded
          applyStyles();
        } else {
          // Wait for metadata to load (earlier than canplay)
          // This ensures styles are applied before first play click
          videoElement.addEventListener("loadedmetadata", applyStyles, {
            once: true,
          });
        }
      }
    };

    setOverlayVisibility(isInlinePlaybackDevice);

    const markPendingPlay = () => {
      pendingPlayRef.current = true;
    };

    const clearPendingPlay = () => {
      pendingPlayRef.current = false;
    };

    const attemptPlayback = () => {
      if (!pendingPlayRef.current || !isActive) {
        return;
      }
      if (!videoElement.paused) {
        clearPendingPlay();
        return;
      }
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    };

    const lockContainerHeight = () => {
      if (!containerRef.current) {
        return;
      }
      const { height } = containerRef.current.getBoundingClientRect();
      if (height > 0) {
        containerRef.current.style.minHeight = `${height}px`;
      }
    };

    const unlockContainerHeight = () => {
      if (containerRef.current) {
        containerRef.current.style.minHeight = "";
      }
      if (isInlinePlaybackDevice && isActive) {
        setOverlayVisibility(false);
      }
    };

    const attachAutoPlayListeners = (videoElement: HTMLVideoElement) => {
      const handleAutoPlay = () => {
        attemptPlayback();
      };
      const handlePlaying = () => {
        clearPendingPlay();
        unlockContainerHeight();
      };
      videoElement.addEventListener("loadedmetadata", handleAutoPlay);
      videoElement.addEventListener("canplay", handleAutoPlay);
      videoElement.addEventListener("playing", handlePlaying);
      return () => {
        videoElement.removeEventListener("loadedmetadata", handleAutoPlay);
        videoElement.removeEventListener("canplay", handleAutoPlay);
        videoElement.removeEventListener("playing", handlePlaying);
      };
    };

    const applyPosterAspectRatio = () => {
      if (!isInlinePlaybackDevice || !containerRef.current) {
        return;
      }
      if (!adaptivePlaylistCover || !videoUrl || !isVideo) {
        return;
      }
      const posterUrl = `${videoUrl}/poster.jpg`;
      const img = new Image();
      img.onload = () => {
        if (!containerRef.current || !img.width || !img.height || !isActive) {
          return;
        }
        containerRef.current.style.aspectRatio = `${img.width} / ${img.height}`;
      };
      img.src = posterUrl;
    };

    const clearPosterAspectRatio = () => {
      if (containerRef.current) {
        containerRef.current.style.aspectRatio = "";
      }
    };

    if (isInlinePlaybackDevice) {
      videoElement.addEventListener("playing", clearPosterAspectRatio, {
        once: true,
      });
    }
    applyPosterAspectRatio();

    if (
      !Hls.isSupported() ||
      adaptivePlaylistCover === false ||
      adaptivePlaylistCover === null ||
      adaptivePlaylistCover === undefined
    ) {
      (async () => {
        enableInlinePlayback();
        disableNativeControls();
        if (adaptivePlaylistCover && videoUrl && isActive) {
          const nativeHlsSource = `${videoUrl}/master_playlist.m3u8`;
          if (!videoElement.getAttribute("src")) {
            videoElement.src = nativeHlsSource;
            videoElement.load();
          }
        }
        const instance = await createPlyr(
          videoElement as unknown as HTMLElement,
          defaultOptions
        );
        if (!isActive) {
          instance?.destroy?.();
          return;
        }
        player.current = instance;
        if (player.current) {
          setupIosStyles();
        }
      })();
      if (isInlinePlaybackDevice) {
        removeAutoPlayListeners = attachAutoPlayListeners(videoElement);
        const handleInlinePlay = () => {
          lockContainerHeight();
          markPendingPlay();
          attemptPlayback();
        };
        requestPlaybackRef.current = handleInlinePlay;
      }
    } else {
      const source = `${videoUrl}/master_playlist.m3u8`;
      const isIosDevice = /iPad|iPhone|iPod/i.test(navigator.userAgent);
      const isInAppBrowser = /Instagram|FBAN|FBAV|Telegram/i.test(
        navigator.userAgent
      );
      const shouldPreload = isIosDevice || isInAppBrowser;
      hls = new Hls({ autoStartLoad: shouldPreload });
      hls.loadSource(source);
      hls.attachMedia(videoElement as unknown as HTMLVideoElement);
      enableInlinePlayback();
      disableNativeControls();
      if (shouldPreload) {
        hls.startLoad();
      }

      let hasStartedLoad = false;
      const startHlsLoad = () => {
        if (!hls || hasStartedLoad) {
          return;
        }
        hasStartedLoad = true;
        hls.startLoad();
      };

      removeAutoPlayListeners = attachAutoPlayListeners(videoElement);

      const requestPlayback = () => {
        lockContainerHeight();
        markPendingPlay();
        startHlsLoad();
        attemptPlayback();
      };
      requestPlaybackRef.current = requestPlayback;

      const handleUserPlayRequest = () => {
        requestPlayback();
      };

      videoElement.addEventListener("click", handleUserPlayRequest);
      removeHlsGestureHandlers = () => {
        videoElement.removeEventListener("click", handleUserPlayRequest);
      };

      hls.on(Hls.Events.MANIFEST_PARSED, function (_event, data) {
        const availableQualities = hls!.levels.map((l) => l.height);
        availableQualities.unshift(0);

        defaultOptions.quality = {
          default: 0,
          options: availableQualities,
          forced: true,
          onChange: (e) => {
            if (e === 0) {
              hls!.currentLevel = -1;
            } else {
              hls!.levels.forEach((level, levelIndex) => {
                if (level.height === e) {
                  hls!.currentLevel = levelIndex;
                }
              });
            }
          },
        };

        defaultOptions.i18n = {
          qualityLabel: {
            0: "Auto",
          },
        };

        (async () => {
          const instance = await createPlyr(
            videoElement as unknown as HTMLElement,
            defaultOptions
          );
          if (!isActive) {
            instance?.destroy?.();
            return;
          }
          player.current = instance;
          if (player.current) {
            setupIosStyles();
            player.current.on("play", requestPlayback);
          }
        })();
      });
    }

    return () => {
      isActive = false;
      requestPlaybackRef.current = null;
      pendingPlayRef.current = false;
      setOverlayVisibility(false);
      videoElement.removeEventListener("playing", clearPosterAspectRatio);
      if (removeHlsGestureHandlers) {
        removeHlsGestureHandlers();
      }
      if (removeAutoPlayListeners) {
        removeAutoPlayListeners();
      }
      if (player.current) {
        player.current.destroy();
      }
      if (hls) {
        hls.destroy();
      }
    };
  }, [videoUrl, adaptivePlaylistCover, isVideo]);

  const poster =
    adaptivePlaylistCover === true && videoUrl && isVideo
      ? `${videoUrl}/poster.jpg`
      : null;

  if (!videoUrl) {
    return null;
  }

  // Render image if it's an image
  if (isImage) {
    return (
      <div className="rounded-lg overflow-hidden">
        <img
          src={videoUrl}
          alt="Event cover"
          className="w-full h-auto object-cover"
        />
      </div>
    );
  }

  // Render video if it's a video
  if (isVideo) {
    return (
      <div
        className="rounded-lg overflow-hidden relative"
        ref={containerRef}
        style={{ touchAction: "pan-y" }}
      >
        <div
          ref={overlayRef}
          className="absolute inset-0 z-10 hidden items-center justify-center pointer-events-none"
        >
          <button
            type="button"
            aria-label="Play video"
            className="pointer-events-auto rounded-full bg-black/60 px-4 py-2 text-sm font-semibold text-white"
            onClick={handleInlineOverlayPlay}
          >
            <svg
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        </div>
        <video
          ref={playerContainer}
          controls
          playsInline
          preload="metadata"
          src={!adaptivePlaylistCover ? videoUrl : undefined}
          poster={poster || undefined}
        />
      </div>
    );
  }

  // Fallback: if we can't determine the type, return null
  return null;
};

EventCoverPlayer.displayName = "EventCoverPlayer";

export default memo(EventCoverPlayer);
